'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  question: string;
  answer: string;
}

const FAQSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const faqs: FAQ[] = [
    {
      question: "Zijn er verborgen kosten of setup fees?",
      answer: "Nee, onze prijzen zijn volledig transparant. Wat u ziet is wat u betaalt. Er zijn geen verborgen kosten, setup fees of onverwachte toeslagen. Alle implementatiekosten zijn inbegrepen in het maandelijkse tarief."
    },
    {
      question: "Kan ik van pakket wisselen tijdens mijn contract?",
      answer: "Ja, u kunt op elk moment upgraden naar een hoger pakket. Bij een downgrade geldt een opzegtermijn van één maand. We helpen u graag bij het vinden van het pakket dat het beste bij uw huidige behoeften past."
    },
    {
      question: "Wat gebeurt er na de 30 dagen geld-terug-garantie?",
      answer: "Na de eerste 30 dagen gaat u over op een maandelijks opzegbaar contract. U kunt op elk moment stoppen met een opzegtermijn van één maand. We zijn ervan overtuigd dat u tevreden zult zijn met onze diensten."
    },
    {
      question: "Hoe werkt de implementatie en hoe lang duurt dit?",
      answer: "De implementatietijd varieert per pakket. Starter: 2-3 weken, Professional: 4-6 weken, Enterprise: 8-12 weken. We beginnen met een grondige analyse van uw processen, gevolgd door configuratie, training en go-live. U blijft gedurende het hele proces volledig operationeel."
    },
    {
      question: "Welke betaalmethoden accepteren jullie?",
      answer: "We accepteren bankoverschrijving, automatische incasso en creditcard betalingen. Voor Enterprise klanten zijn er ook mogelijkheden voor kwartaal- of jaarfacturering met aantrekkelijke kortingen."
    },
    {
      question: "Is er een minimale contractduur?",
      answer: "Voor de Starter en Professional pakketten is er geen minimale contractduur na de eerste maand. Enterprise contracten hebben doorgaans een minimale looptijd van 12 maanden vanwege de uitgebreide maatwerk implementatie."
    },
    {
      question: "Wat is inbegrepen in de support?",
      answer: "Alle pakketten bevatten e-mail support tijdens kantooruren. Professional en Enterprise krijgen ook telefoon support. Enterprise klanten hebben daarnaast een dedicated account manager en priority support met gegarandeerde responstijden."
    },
    {
      question: "Kunnen we een demo krijgen voordat we beslissen?",
      answer: "Absoluut! We bieden gratis demo's aan waarin we u door onze oplossingen leiden en laten zien hoe deze specifiek voor uw bedrijf kunnen werken. U kunt direct een demo boeken via onze website."
    }
  ];

  const toggleFAQ = (index: number) => {
    if (!isHydrated) return;
    setOpenIndex(openIndex === index ? null : index);
  };

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
                Veelgestelde vragen
              </h2>
              <p className="text-lg text-muted-foreground">
                Laden...
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
              Veelgestelde vragen
            </h2>
            <p className="text-lg text-muted-foreground">
              Antwoorden op de meest gestelde vragen over onze prijzen en diensten
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="glassmorphic rounded-xl overflow-hidden transition-all duration-300"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-primary/5 transition-colors duration-300"
                >
                  <span className="font-semibold text-foreground pr-4">
                    {faq.question}
                  </span>
                  <Icon
                    name="ChevronDownIcon"
                    size={20}
                    className={`text-primary flex-shrink-0 transition-transform duration-300 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-6 pb-4 text-muted-foreground">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-muted-foreground mb-4">
              Heeft u nog andere vragen?
            </p>
            <Link
              href="/contact"
              className="inline-flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300"
            >
              <span>Neem contact op</span>
              <Icon name="ArrowRightIcon" size={20} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;